import * as React from 'react';
import styled, { css } from 'styled-components';

import { createUIContext } from '../../internals/createContext';
import { composeRefs } from '../../internals/composeRefs';
import { useLayerZIndex } from '../../internals/LayerManager';
import { Presence } from '../../internals/Presence';
import { Portal } from '../../internals/Portal';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { Slot } from '../../internals/Slot';
import { useControllable } from '../../internals/useControllable';
import { useEscapeKey } from '../../internals/useEscapeKey';
import { useFocusTrap } from '../../internals/useFocusTrap';
import { useId } from '../../internals/useId';
import { useScrollLock } from '../../internals/useScrollLock';
import { useTheme } from '../../theme/ThemeProvider';
import { Spinner } from '../../primitives/Spinner/Spinner';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';

/* ─────────────────────────── Types ────────────────────────────────────── */

export type ModalSize =
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | 'xxl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | 'full';

/* ─────────────────────────── Context ──────────────────────────────────── */

export interface ModalRootProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

interface ModalContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  titleId: string;
  descriptionId: string;
}

const [ModalProvider, useModalContext] = createUIContext<ModalContextValue>('Modal');

/* ─────────────────────────── Root ──────────────────────────────────────── */

const ModalRoot = ({ open, defaultOpen, onOpenChange, children }: ModalRootProps) => {
  const [isOpen, setOpen] = useControllable<boolean>({
    value: open,
    defaultValue: defaultOpen ?? false,
    onChange: onOpenChange,
  });
  const titleId = useId('modal-title');
  const descriptionId = useId('modal-description');

  const value = React.useMemo(
    () => ({ open: isOpen, onOpenChange: setOpen, titleId, descriptionId }),
    [isOpen, setOpen, titleId, descriptionId]
  );

  return <ModalProvider value={value}>{children}</ModalProvider>;
};

ModalRoot.displayName = 'Modal.Root';

/* ─────────────────────────── Trigger ──────────────────────────────────── */

export interface ModalTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  semanticId?: string;
}

const ModalTrigger = React.forwardRef<HTMLButtonElement, ModalTriggerProps>(function ModalTrigger(
  { asChild = false, semanticId, onClick, type = 'button', children, ...rest },
  ref
) {
  const { open, onOpenChange } = useModalContext();
  const semanticProps = getSemanticAttributes(semanticId);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    if (!event.defaultPrevented) onOpenChange(true);
  };

  if (asChild) {
    return (
      <Slot
        ref={ref as React.Ref<HTMLElement>}
        data-state={open ? 'open' : 'closed'}
        onClick={handleClick as React.MouseEventHandler<HTMLElement>}
        {...semanticProps}
        {...(rest as unknown as React.ComponentProps<typeof Slot>)}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      data-state={open ? 'open' : 'closed'}
      onClick={handleClick}
      {...semanticProps}
      {...rest}>
      {children}
    </button>
  );
});

ModalTrigger.displayName = 'Modal.Trigger';

/* ─────────────────────────── Portal ──────────────────────────────────── */

export interface ModalPortalProps {
  children: React.ReactNode;
}

/**
 * Full-viewport stacking root. `isolation: isolate` scopes internal z-indexes.
 * Re-injects theme CSS variables so token-based styles work outside ThemeProvider DOM.
 * z-index is applied dynamically via LayerManager to support modal-over-modal stacking.
 */
const ModalPortalStack = styled.div`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  isolation: isolate;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

const ModalPortal = ({ children }: ModalPortalProps) => {
  const { theme } = useTheme();
  const layerZ = useLayerZIndex('modal');
  return (
    <Portal>
      <ModalPortalStack
        data-wl-modal-stack=''
        style={{ ...theme.cssVars as React.CSSProperties, zIndex: layerZ }}
      >
        {children}
      </ModalPortalStack>
    </Portal>
  );
};

ModalPortal.displayName = 'Modal.Portal';

/* ─────────────────────────── Overlay ─────────────────────────────────── */

const StyledOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0;
  background: var(--ui-overlay, rgba(0, 0, 0, 0.85));
  opacity: 0;
  transition: opacity 0.35s ease;

  &[data-state='open'] {
    opacity: 1;
  }
`;

export interface ModalOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  /** When true, clicking the dark backdrop area closes the modal. */
  closeOnOverlayClick?: boolean;
  semanticId?: string;
}

const ModalOverlay = React.forwardRef<HTMLDivElement, ModalOverlayProps>(function ModalOverlay(
  { closeOnOverlayClick = false, semanticId, onClick, children, ...rest },
  ref
) {
  const { open, onOpenChange } = useModalContext();
  const semanticProps = getSemanticAttributes(semanticId);

  useScrollLock(open);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    onClick?.(event);
    if (!event.defaultPrevented && closeOnOverlayClick) {
      onOpenChange(false);
    }
  };

  return (
    <>
      <Presence present={open} animateEnter>
        <StyledOverlay ref={ref} onClick={handleClick} {...semanticProps} {...rest} />
      </Presence>
      {children}
    </>
  );
});

ModalOverlay.displayName = 'Modal.Overlay';

/* ─────────────────────────── Content (the panel) ─────────────────────── */

const SIZE_MAP: Record<ModalSize, ReturnType<typeof css>> = {
  sm: css`
    @media (min-width: 768px) {
      width: 400px;
    }
  `,
  md: css`
    @media (min-width: 768px) {
      width: 500px;
    }
  `,
  lg: css`
    @media (min-width: 768px) {
      width: 700px;
    }
  `,
  xl: css`
    @media (min-width: 768px) {
      width: 700px;
    }
    @media (min-width: 1024px) {
      width: 800px;
    }
  `,
  xxl: css`
    @media (min-width: 768px) {
      width: 700px;
    }
    @media (min-width: 1024px) {
      width: 900px;
    }
  `,
  '3xl': css`
    @media (min-width: 768px) {
      width: 700px;
    }
    @media (min-width: 1024px) {
      width: 1114px;
    }
  `,
  '4xl': css`
    @media (min-width: 768px) {
      width: 1000px;
    }
    @media (min-width: 1024px) {
      width: 1240px;
    }
  `,
  '5xl': css`
    @media (min-width: 768px) {
      width: 1200px;
    }
    @media (min-width: 1024px) {
      width: 1440px;
    }
  `,
  '6xl': css`
    @media (min-width: 768px) {
      width: 1400px;
    }
    @media (min-width: 1024px) {
      width: 1680px;
    }
  `,
  '7xl': css`
    @media (min-width: 768px) {
      width: 1600px;
    }
    @media (min-width: 1024px) {
      width: 1920px;
    }
  `,
  full: css`
    width: 90vw !important;
    max-width: 90vw !important;
  `,
};

const StyledPanel = styled.div<{ $size: ModalSize }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  background: var(--ui-surface, ${palette.White});
  color: var(--ui-text, inherit);
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  width: calc(100% - ${rem(32)});
  height: calc(100% - ${rem(32)});
  display: flex;
  flex-direction: column;
  border-radius: var(--ui-radius-lg, ${rem(10)});
  margin: ${rem(16)};
  overflow: auto;
  -webkit-font-smoothing: antialiased;

  transform: translateX(100%);
  transition: transform 0.35s ease;

  &[data-state='open'] {
    transform: translateX(0);
  }

  ${({ $size }) => SIZE_MAP[$size] ?? ''}
`;

export interface ModalContentProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: ModalSize;
  semanticId?: string;
}

const ModalContent = React.forwardRef<HTMLDivElement, ModalContentProps>(function ModalContent(
  { size = 'md', semanticId, children, ...rest },
  ref
) {
  const { open, onOpenChange, titleId, descriptionId } = useModalContext();
  const focusTrapRef = useFocusTrap(open);

  const close = React.useCallback(() => onOpenChange(false), [onOpenChange]);
  useEscapeKey(close, open);

  const composedRef = composeRefs(ref, focusTrapRef);
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <Presence present={open} animateEnter>
      <StyledPanel
        ref={composedRef}
        $size={size}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        {...semanticProps}
        {...rest}>
        {children}
      </StyledPanel>
    </Presence>
  );
});

ModalContent.displayName = 'Modal.Content';

/* ─────────────────────────── Header ──────────────────────────────────── */

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: ${rem(1)} solid var(--ui-border-muted, ${palette.CulturedWhite});
  padding: ${rem(24)} ${rem(32)} ${rem(21)} ${rem(32)};
  align-items: flex-start;
`;

export interface ModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const ModalHeader = React.forwardRef<HTMLDivElement, ModalHeaderProps>(function ModalHeader(
  { semanticId, children, ...rest },
  ref
) {
  const semanticProps = getSemanticAttributes(semanticId);
  return (
    <StyledHeader ref={ref} {...semanticProps} {...rest}>
      {children}
    </StyledHeader>
  );
});

ModalHeader.displayName = 'Modal.Header';

/* ─────────────────────────── HeaderText ───────────────────────────────── */

const StyledHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

/* ─────────────────────────── Title ───────────────────────────────────── */

export interface ModalTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  semanticId?: string;
}

const StyledTitle = styled.h4`
  font-weight: 500;
  font-size: ${rem(22)};
  line-height: ${rem(25)};
  margin: 0;
  color: var(--ui-text, ${palette.Black});
`;

const ModalTitle = React.forwardRef<HTMLHeadingElement, ModalTitleProps>(function ModalTitle(
  { semanticId, ...rest },
  ref
) {
  const { titleId } = useModalContext();
  const semanticProps = getSemanticAttributes(semanticId);
  return <StyledTitle ref={ref} {...semanticProps} {...rest} id={titleId} />;
});

ModalTitle.displayName = 'Modal.Title';

/* ─────────────────────────── Subtitle ────────────────────────────────── */

export interface ModalSubtitleProps extends React.HTMLAttributes<HTMLParagraphElement> {
  semanticId?: string;
}

const StyledSubtitle = styled.p`
  font-size: ${rem(14)};
  line-height: ${rem(20)};
  font-weight: 400;
  margin: ${rem(4)} 0 0 0;
  color: var(--ui-text, ${palette.Black});
`;

const ModalSubtitle = React.forwardRef<HTMLParagraphElement, ModalSubtitleProps>(
  function ModalSubtitle({ semanticId, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return <StyledSubtitle ref={ref} {...semanticProps} {...rest} />;
  }
);

ModalSubtitle.displayName = 'Modal.Subtitle';

/* ─────────────────────────── Description ─────────────────────────────── */

export interface ModalDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  semanticId?: string;
}

const ModalDescription = React.forwardRef<HTMLParagraphElement, ModalDescriptionProps>(
  function ModalDescription({ semanticId, ...rest }, ref) {
    const { descriptionId } = useModalContext();
    const semanticProps = getSemanticAttributes(semanticId);
    return <p ref={ref} {...semanticProps} {...rest} id={descriptionId} />;
  }
);

ModalDescription.displayName = 'Modal.Description';

/* ─────────────────────────── Close ───────────────────────────────────── */

export interface ModalCloseProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const StyledClose = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${rem(2)};
  cursor: pointer;
  flex-shrink: 0;

  span {
    transition: all 0.5s 0s ease;
  }

  &:hover span {
    color: var(--ui-danger, ${palette.Red});
  }

  .icon-color {
    color: var(--ui-text-subtle, ${palette.Silver});
  }
`;

const StyledCloseLabel = styled.span`
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  color: var(--ui-text-muted, ${palette.SpanishGray});
`;

const ModalClose = React.forwardRef<HTMLDivElement, ModalCloseProps>(function ModalClose(
  { semanticId, onClick, children, ...rest },
  ref
) {
  const { onOpenChange } = useModalContext();
  const semanticProps = getSemanticAttributes(semanticId);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    onClick?.(event);
    if (!event.defaultPrevented) onOpenChange(false);
  };

  return (
    <StyledClose ref={ref} onClick={handleClick} {...semanticProps} {...rest}>
      {children ?? (
        <>
          <span className='material-symbols-outlined icon-color'>cancel</span>
          <StyledCloseLabel>Close</StyledCloseLabel>
        </>
      )}
    </StyledClose>
  );
});

ModalClose.displayName = 'Modal.Close';

/* ─────────────────────────── Body ────────────────────────────────────── */

const StyledBody = styled.div`
  padding: ${rem(24)} ${rem(32)};
  overflow-y: auto;
  flex: 1;

  ::-webkit-scrollbar {
    width: 5px;
    height: 5px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: var(--ui-radius-md, 7px);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--ui-text-subtle, ${palette.SpanishGray});
    border-radius: var(--ui-radius-md, 7px);
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const StyledLoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: ${rem(300)};
`;

export interface ModalBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Shows a centered spinner instead of children. */
  loading?: boolean;
  semanticId?: string;
}

const ModalBody = React.forwardRef<HTMLDivElement, ModalBodyProps>(function ModalBody(
  { loading = false, semanticId, children, ...rest },
  ref
) {
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <StyledBody ref={ref} {...semanticProps} {...rest}>
      {loading ? (
        <StyledLoaderContainer>
          <Spinner size='lg' />
        </StyledLoaderContainer>
      ) : (
        children
      )}
    </StyledBody>
  );
});

ModalBody.displayName = 'Modal.Body';

/* ─────────────────────────── Footer ──────────────────────────────────── */

const StyledFooter = styled.div`
  box-sizing: border-box;
  width: 100%;
  border-top: ${rem(1)} solid var(--ui-border-muted, ${palette.CulturedWhite});
  background-color: var(--ui-surface, ${palette.White});
  padding: ${rem(20)} ${rem(32)};
  border-bottom-right-radius: var(--ui-radius-lg, ${rem(10)});
  border-bottom-left-radius: var(--ui-radius-lg, ${rem(10)});
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export interface ModalFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const ModalFooter = React.forwardRef<HTMLDivElement, ModalFooterProps>(function ModalFooter(
  { semanticId, children, ...rest },
  ref
) {
  const semanticProps = getSemanticAttributes(semanticId);
  return (
    <StyledFooter ref={ref} {...semanticProps} {...rest}>
      {children}
    </StyledFooter>
  );
});

ModalFooter.displayName = 'Modal.Footer';

/* ─────────────────────────── Compound export ──────────────────────────── */

export const Modal = {
  Root: ModalRoot,
  Trigger: ModalTrigger,
  Portal: ModalPortal,
  Overlay: ModalOverlay,
  Content: ModalContent,
  Header: ModalHeader,
  HeaderText: StyledHeaderText,
  Body: ModalBody,
  Footer: ModalFooter,
  Close: ModalClose,
  Title: ModalTitle,
  Subtitle: ModalSubtitle,
  Description: ModalDescription,
};
